import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { Source, Layer, ScaleControl } from "react-map-gl";
import ClientAssetDataMap, { normalizeRootGeoJSON, normalizeClientAssetData } from "../ClientAssetDataMap";
import Button from "@mui/material/Button";

export const DEFAULT_ZOOM = 7;

// Plasma color gradient (reversed: dark = low wspd, bright = high wspd)
const WSPD_COLORS = [
    [13, 8, 135],
    [84, 2, 163],
    [155, 23, 156],
    [209, 81, 107],
    [244, 159, 45],
    [240, 249, 33],
];
const WSPD_COLOR_KEY = `linear-gradient(to right, ${WSPD_COLORS.map(([r, g, b]) => `rgb(${r},${g},${b})`).join(", ")})`;

function getWindSpeedColor(speed, minSpeed, maxSpeed) {
    if (speed == null || !isFinite(speed) || minSpeed === maxSpeed) return "#1976d2";
    const t = Math.max(0, Math.min(1, (speed - minSpeed) / (maxSpeed - minSpeed)));
    const idx = t * (WSPD_COLORS.length - 1);
    const lo = Math.floor(idx);
    const hi = Math.min(lo + 1, WSPD_COLORS.length - 1);
    const f = idx - lo;
    const [r, g, b] = WSPD_COLORS[lo].map((c, i) => Math.round(c + f * (WSPD_COLORS[hi][i] - c)));
    return `rgb(${r},${g},${b})`;
}

const useStyles = makeStyles(() => ({
    mapPanel: {
        flex: 3,
    },
}));

function assignOffsets(spans) {
    const groups = {};
    for (const span of spans) {
        const [a, b] = [span.start_structure, span.end_structure].sort();
        const key = `${a}__${b}`;
        if (!groups[key]) groups[key] = [];
        groups[key].push(span);
    }
    return spans.map((span) => {
        const [a, b] = [span.start_structure, span.end_structure].sort();
        const group = groups[`${a}__${b}`];
        const idx = group.indexOf(span);
        return { span, offset: (idx - (group.length - 1) / 2) * 6 };
    });
}

export default function AspenMap({
    mapRef,
    rootGeoJSON,
    clientAssetData,
    structures,
    ineligibleStructureFromDataset,
    ineligibleStructureFromUser,
    setIneligibleStructureFromUser,
    windSpeedsByStructure,
    sensorPlacements,
    expandedSensorName,
    pendingStructureName,
    pendingInstallOptionIdx,
    onSensorClick,
    onSubmitRefinement,
    showUncoverableLines = false,
    showUncoveredCoverableLines = false,
    showUncoveredSpans = false,
    uncoveredSpanIds,
    uncoverableLineIds,
    uncoveredLineIds,
    uncoveredLineSets = [],
    wspdHighlightView = null,
}) {
    const classes = useStyles();
    const dragStartRef = useRef(null);
    const [boxSelectMode, setBoxSelectMode] = useState(false);
    const [dragStart, setDragStart] = useState(null);
    const [dragCurrent, setDragCurrent] = useState(null);
    const [selectedStructureNames, setSelectedStructureNames] = useState(new Set());
    const [showSpanWindSpeed, setShowSpanWindSpeed] = useState(true);
    const [showStructures, setShowStructures] = useState(false);
    const [accessibleStructureFromUser, setAccessibleStructureFromUser] = useState(new Set());

    // rootGeoJSON is the newer asset data format; clientAssetData is kept for
    // projects that haven't been migrated to root-geojson yet. rootGeoJSON wins
    // when both are present.
    const assetData = useMemo(
        () => normalizeRootGeoJSON(rootGeoJSON) ?? normalizeClientAssetData(clientAssetData),
        [rootGeoJSON, clientAssetData]
    );

    useEffect(() => {
        if (showUncoverableLines || showUncoveredCoverableLines || showUncoveredSpans) setShowSpanWindSpeed(false);
    }, [showUncoverableLines, showUncoveredCoverableLines, showUncoveredSpans]);

    const centerPosition =
        structures.length > 0
            ? {
                  latitude: structures.reduce((sum, s) => sum + s.latitude, 0) / structures.length,
                  longitude: structures.reduce((sum, s) => sum + s.longitude, 0) / structures.length,
              }
            : null;

    const selectedStructuresGeoJSON = {
        type: "FeatureCollection",
        features: structures
            .filter((s) => selectedStructureNames.has(s.structure_name))
            .map((s) => ({
                type: "Feature",
                geometry: { type: "Point", coordinates: [s.longitude, s.latitude] },
                properties: {},
            })),
    };

    const inaccessibleStructuresGeoJSON = {
        type: "FeatureCollection",
        features: structures
            .filter((s) => ineligibleStructureFromUser.has(s.structure_name))
            .map((s) => ({
                type: "Feature",
                geometry: { type: "Point", coordinates: [s.longitude, s.latitude] },
                properties: { structure_name: s.structure_name },
            })),
    };

    const ineligibleStructuresGeoJSON = {
        type: "FeatureCollection",
        features: structures
            .filter(
                (s) =>
                    s.structure_name in ineligibleStructureFromDataset &&
                    !accessibleStructureFromUser.has(s.structure_name)
            )
            .map((s) => ({
                type: "Feature",
                geometry: { type: "Point", coordinates: [s.longitude, s.latitude] },
                properties: {
                    structure_name: s.structure_name,
                    reason: ineligibleStructureFromDataset[s.structure_name],
                    description: `${s.structure_name}: ${ineligibleStructureFromDataset[s.structure_name]}`,
                },
            })),
    };

    const accessibleOverrideStructuresGeoJSON = {
        type: "FeatureCollection",
        features: structures
            .filter((s) => accessibleStructureFromUser.has(s.structure_name))
            .map((s) => ({
                type: "Feature",
                geometry: { type: "Point", coordinates: [s.longitude, s.latitude] },
                properties: {},
            })),
    };

    const sensorPlacementGeoJSON = {
        type: "FeatureCollection",
        features: sensorPlacements.map((placement) => {
            const overrideStructureName =
                placement.sensor_name === expandedSensorName && pendingStructureName !== placement.structure_name
                    ? pendingStructureName
                    : null;
            const overrideStructure = overrideStructureName
                ? structures.find((s) => s.structure_name === overrideStructureName)
                : null;
            return {
                type: "Feature",
                geometry: {
                    type: "Point",
                    coordinates: overrideStructure
                        ? [overrideStructure.longitude, overrideStructure.latitude]
                        : [placement.structure_longitude, placement.structure_latitude],
                },
                properties: {
                    sensor_name: placement.sensor_name,
                    sensor_color: placement.sensor_color,
                    description: `Sensor ${placement.sensor_name} - Structure ${overrideStructureName ?? placement.structure_name}`,
                },
            };
        }),
    };

    const originalPlacementGeoJSON = {
        type: "FeatureCollection",
        features: sensorPlacements
            .filter(
                (placement) =>
                    placement.sensor_name === expandedSensorName && pendingStructureName !== placement.structure_name
            )
            .map((placement) => ({
                type: "Feature",
                geometry: {
                    type: "Point",
                    coordinates: [placement.structure_longitude, placement.structure_latitude],
                },
                properties: {
                    sensor_name: placement.sensor_name,
                    sensor_color: placement.sensor_color,
                },
            })),
    };

    const allSensorSpansGeoJSON = (() => {
        const features = sensorPlacements.flatMap((placement) => {
            const isExpanded = placement.sensor_name === expandedSensorName;
            const installOptionIdx = isExpanded ? pendingInstallOptionIdx : placement.user_config.install_option_idx;
            const installOption = placement.sorted_install_options[installOptionIdx];
            if (!installOption) return [];
            const endStructure = structures.find((s) => s.structure_name === installOption.span_end_structure_name);
            if (!endStructure) return [];
            if (placement.sensor_name === expandedSensorName && pendingStructureName !== placement.structure_name)
                return [];
            const startCoords = [installOption.structure_longitude, installOption.structure_latitude];
            const quarterCoords = [
                installOption.structure_longitude + 0.25 * (endStructure.longitude - installOption.structure_longitude),
                installOption.structure_latitude + 0.25 * (endStructure.latitude - installOption.structure_latitude),
            ];
            return [
                {
                    type: "Feature",
                    geometry: {
                        type: "LineString",
                        coordinates: [startCoords, quarterCoords],
                    },
                    properties: {
                        sensor_name: placement.sensor_name,
                        sensor_color: placement.sensor_color,
                        span_color: placement.sensor_color,
                    },
                },
            ];
        });
        return { type: "FeatureCollection", features };
    })();

    const { spanWspdGeoJSON, minSpanWindSpeed, maxSpanWindSpeed } = (() => {
        const coordMap = Object.fromEntries(structures.map((s) => [s.structure_name, [s.longitude, s.latitude]]));
        const spanEndpoints = {};
        const spanToWspd = {};
        Object.entries(windSpeedsByStructure).forEach(([structureName, spans]) => {
            if (typeof spans !== "object" || Array.isArray(spans) || spans === null) return;
            Object.entries(spans).forEach(([spanId, wspd]) => {
                if (!spanEndpoints[spanId]) {
                    spanEndpoints[spanId] = new Set();
                    spanToWspd[spanId] = wspd;
                }
                spanEndpoints[spanId].add(structureName);
            });
        });

        const spanList = Object.entries(spanEndpoints).flatMap(([spanId, structureSet]) => {
            const [s1, s2] = [...structureSet];
            if (!s2) return [];
            const c1 = coordMap[s1];
            const c2 = coordMap[s2];
            if (!c1 || !c2) return [];
            return [{ spanId, s1, s2, c1, c2 }];
        });

        const wspdList = spanList.map((span) => spanToWspd[span.spanId]);
        const minWspd = wspdList.length ? Math.min(...wspdList) : 0;
        const maxWspd = wspdList.length ? Math.max(...wspdList) : 0;

        const groups = {};
        for (const span of spanList) {
            const key = [span.s1, span.s2].sort().join("__");
            if (!groups[key]) groups[key] = [];
            groups[key].push(span);
        }

        const SPAN_OFFSET_PX = 6;
        const features = spanList.map((span) => {
            const key = [span.s1, span.s2].sort().join("__");
            const group = groups[key];
            const offset = (group.indexOf(span) - (group.length - 1) / 2) * SPAN_OFFSET_PX;
            return {
                type: "Feature",
                geometry: { type: "LineString", coordinates: [span.c1, span.c2] },
                properties: {
                    span_color: getWindSpeedColor(spanToWspd[span.spanId], minWspd, maxWspd),
                    description: `${spanToWspd[span.spanId].toFixed(2)} m/s\n${span.spanId}`,
                    offset,
                    span_id: span.spanId,
                    wspd: spanToWspd[span.spanId],
                },
            };
        });

        return {
            spanWspdGeoJSON: { type: "FeatureCollection", features },
            minSpanWindSpeed: minWspd,
            maxSpanWindSpeed: maxWspd,
        };
    })();

    const allCoverageSpansGeoJSON = useMemo(() => {
        if (!showUncoveredSpans) return { type: "FeatureCollection", features: [] };
        return {
            type: "FeatureCollection",
            features: spanWspdGeoJSON.features
                .filter((f) => uncoveredSpanIds?.has(f.properties.span_id))
                .map((f) => ({
                    ...f,
                    properties: { ...f.properties, span_color: "#c62828" },
                })),
        };
    }, [showUncoveredSpans, uncoveredSpanIds, spanWspdGeoJSON]);

    const problemLinesSpansGeoJSON = useMemo(() => {
        if (!showUncoverableLines && !showUncoveredCoverableLines) return { type: "FeatureCollection", features: [] };
        const allSections = assetData?.sections ?? [];
        const uncoverableSectionNames = showUncoverableLines
            ? new Set(allSections.filter((s) => uncoverableLineIds?.has(s.line_name)).map((s) => s.section_name))
            : new Set();
        const uncoveredSectionNames = showUncoveredCoverableLines
            ? new Set(allSections.filter((s) => uncoveredLineIds?.has(s.line_name)).map((s) => s.section_name))
            : new Set();
        const allSpans = assetData?.spans ?? [];
        const matching = allSpans.filter(
            (s) => uncoverableSectionNames.has(s.section_name) || uncoveredSectionNames.has(s.section_name)
        );
        return {
            type: "FeatureCollection",
            features: assignOffsets(matching)
                .map(({ span, offset }) => {
                    const startStr = structures.find((s) => s.structure_name === span.start_structure);
                    const endStr = structures.find((s) => s.structure_name === span.end_structure);
                    if (!startStr || !endStr) return null;
                    return {
                        type: "Feature",
                        geometry: {
                            type: "LineString",
                            coordinates: [
                                [startStr.longitude, startStr.latitude],
                                [endStr.longitude, endStr.latitude],
                            ],
                        },
                        properties: {
                            offset,
                            span_color: uncoverableSectionNames.has(span.section_name) ? "#bf360c" : "#ef6c00",
                        },
                    };
                })
                .filter(Boolean),
        };
    }, [showUncoverableLines, showUncoveredCoverableLines, uncoverableLineIds, uncoveredLineIds, assetData, structures]);

    const problemLinesIconsGeoJSON = useMemo(() => {
        if (!showUncoverableLines && !showUncoveredCoverableLines) return { type: "FeatureCollection", features: [] };
        const allSections = assetData?.sections ?? [];
        const allSpans = assetData?.spans ?? [];

        function centroidForLines(lineIds) {
            const coords = lineIds.flatMap((lineId) => {
                const sectionNames = new Set(
                    allSections.filter((s) => s.line_name === lineId).map((s) => s.section_name)
                );
                return allSpans
                    .filter((s) => sectionNames.has(s.section_name))
                    .flatMap((span) => {
                        const start = structures.find((s) => s.structure_name === span.start_structure);
                        const end = structures.find((s) => s.structure_name === span.end_structure);
                        return [start, end].filter(Boolean);
                    });
            });
            if (!coords.length) return null;
            return {
                lng: coords.reduce((sum, s) => sum + s.longitude, 0) / coords.length,
                lat: coords.reduce((sum, s) => sum + s.latitude, 0) / coords.length,
            };
        }

        const lineEntries = [];

        if (showUncoverableLines) {
            for (const lineId of (uncoverableLineIds ?? [])) {
                const center = centroidForLines([lineId]);
                if (!center) continue;
                lineEntries.push({ lineNames: [lineId], isUncoverable: true, ...center });
            }
        }

        if (showUncoveredCoverableLines) {
            for (const lineSet of uncoveredLineSets) {
                const center = centroidForLines(lineSet);
                if (!center) continue;
                lineEntries.push({ lineNames: lineSet, isUncoverable: false, ...center });
            }
        }

        return {
            type: "FeatureCollection",
            features: lineEntries.map(({ lineNames, isUncoverable, lng, lat }) => ({
                type: "Feature",
                geometry: { type: "Point", coordinates: [lng, lat] },
                properties: {
                    icon_color: isUncoverable ? "#bf360c" : "#ef6c00",
                    description: isUncoverable
                        ? `Uncoverable line:\n${lineNames.join("\n")}`
                        : `Missing sensor for line(s):\n${lineNames.join("\n")}`,
                },
            })),
        };
    }, [showUncoverableLines, showUncoveredCoverableLines, uncoverableLineIds, uncoveredLineSets, assetData, structures]);

    const { wspdHighlightGeoJSON, wspdHighlightSensorsGeoJSON } = useMemo(() => {
        const empty = { type: "FeatureCollection", features: [] };
        if (!wspdHighlightView) return { wspdHighlightGeoJSON: empty, wspdHighlightSensorsGeoJSON: empty };
        const speeds = spanWspdGeoJSON.features.map((f) => f.properties.wspd).sort((a, b) => a - b);
        if (!speeds.length) return { wspdHighlightGeoJSON: empty, wspdHighlightSensorsGeoJSON: empty };
        const pctVal = (p) => speeds[Math.max(0, Math.ceil(speeds.length * p) - 1)];
        const p10 = pctVal(0.10), p20 = pctVal(0.20), p50 = pctVal(0.50);
        const inBucket = (w) => {
            if (wspdHighlightView === "wspd_p10")    return w <= p10;
            if (wspdHighlightView === "wspd_p10_20") return w > p10 && w <= p20;
            if (wspdHighlightView === "wspd_p50")    return w <= p50;
            return false;
        };
        const spanFeatures = spanWspdGeoJSON.features.filter((f) => inBucket(f.properties.wspd));
        const sensorFeatures = sensorPlacements.flatMap((placement) => {
            const spans = windSpeedsByStructure[placement.structure_name];
            if (!spans || typeof spans !== "object" || Array.isArray(spans)) return [];
            const vals = Object.values(spans);
            if (!vals.length) return [];
            const wspd = vals.reduce((a, b) => a + b, 0) / vals.length;
            if (!inBucket(wspd)) return [];
            return [{
                type: "Feature",
                geometry: { type: "Point", coordinates: [placement.structure_longitude, placement.structure_latitude] },
                properties: {},
            }];
        });
        return {
            wspdHighlightGeoJSON: {
                type: "FeatureCollection",
                features: spanFeatures.map((f) => ({ ...f, properties: { ...f.properties, span_color: "#00bcd4" } })),
            },
            wspdHighlightSensorsGeoJSON: { type: "FeatureCollection", features: sensorFeatures },
        };
    }, [wspdHighlightView, spanWspdGeoJSON, sensorPlacements, windSpeedsByStructure]);

    const handleOverlayWheel = useCallback(
        (e) => {
            const canvas = mapRef.current?.getMap()?.getCanvas();
            if (!canvas) return;
            canvas.dispatchEvent(
                new WheelEvent("wheel", {
                    bubbles: true,
                    cancelable: true,
                    deltaX: e.deltaX,
                    deltaY: e.deltaY,
                    deltaZ: e.deltaZ,
                    deltaMode: e.deltaMode,
                    clientX: e.clientX,
                    clientY: e.clientY,
                    ctrlKey: e.ctrlKey,
                })
            );
        },
        [mapRef]
    );

    const handleOverlayMouseDown = useCallback((e) => {
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const pt = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        dragStartRef.current = pt;
        setDragStart(pt);
        setDragCurrent(pt);
    }, []);

    const handleOverlayMouseMove = useCallback((e) => {
        if (!dragStartRef.current) return;
        const rect = e.currentTarget.getBoundingClientRect();
        setDragCurrent({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    }, []);

    const handleOverlayMouseUp = useCallback(
        (e) => {
            if (!dragStartRef.current) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const end = { x: e.clientX - rect.left, y: e.clientY - rect.top };
            const start = dragStartRef.current;
            dragStartRef.current = null;
            setDragStart(null);
            setDragCurrent(null);

            const minX = Math.min(start.x, end.x);
            const maxX = Math.max(start.x, end.x);
            const minY = Math.min(start.y, end.y);
            const maxY = Math.max(start.y, end.y);
            if (maxX - minX < 5 && maxY - minY < 5) return;

            const map = mapRef.current?.getMap();
            if (!map) return;
            const sw = map.unproject([minX, maxY]);
            const ne = map.unproject([maxX, minY]);
            const inBox = structures
                .filter(
                    (s) =>
                        s.longitude >= sw.lng && s.longitude <= ne.lng && s.latitude >= sw.lat && s.latitude <= ne.lat
                )
                .map((s) => s.structure_name);
            setIneligibleStructureFromUser((prev) => {
                const next = new Set(prev);
                inBox.forEach((n) => {
                    if (n in ineligibleStructureFromDataset) return;
                    if (next.has(n)) {
                        next.delete(n);
                    } else {
                        next.add(n);
                    }
                });
                return next;
            });
            setAccessibleStructureFromUser((prev) => {
                const next = new Set(prev);
                inBox.forEach((n) => {
                    if (!(n in ineligibleStructureFromDataset)) return;
                    if (next.has(n)) {
                        next.delete(n);
                    } else {
                        next.add(n);
                    }
                });
                return next;
            });
        },
        [
            structures,
            ineligibleStructureFromDataset,
            mapRef,
            setIneligibleStructureFromUser,
            setAccessibleStructureFromUser,
        ]
    );

    const handleMapClick = useCallback(
        (event) => {
            const sensorFeature = event.features?.find((f) => f.layer.id === "sensor-placement-layer");
            if (sensorFeature) {
                onSensorClick(sensorFeature.properties.sensor_name);
            }
        },
        [onSensorClick]
    );

    const handleZoomToFit = useCallback(() => {
        if (structures.length === 0) return;
        const lngs = structures.map((s) => s.longitude);
        const lats = structures.map((s) => s.latitude);
        mapRef.current?.fitBounds(
            [
                [Math.min(...lngs), Math.min(...lats)],
                [Math.max(...lngs), Math.max(...lats)],
            ],
            { padding: 40 }
        );
    }, [structures, mapRef]);

    useEffect(() => {
        if (!dragStart) return;
        const handler = () => {
            dragStartRef.current = null;
            setDragStart(null);
            setDragCurrent(null);
        };
        window.addEventListener("mouseup", handler);
        return () => window.removeEventListener("mouseup", handler);
    }, [dragStart]);

    useEffect(() => {
        if (!boxSelectMode) setSelectedStructureNames(new Set());
    }, [boxSelectMode]);


    return (
        <div className={classes.mapPanel} style={{ position: "relative" }}>
            {!assetData ? (
                <div
                    style={{
                        width: "100%",
                        height: "100%",
                        textAlign: "center",
                        border: "1px solid #dddddd",
                        backgroundColor: "#eeeeee",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <b>Select a client & project</b>
                </div>
            ) : centerPosition ? (
                <>
                    <ClientAssetDataMap
                        ref={mapRef}
                        rootGeoJSON={rootGeoJSON}
                        clientAssetData={clientAssetData}
                        hideSpans={showSpanWindSpeed && spanWspdGeoJSON.features.length > 0}
                        hideStructures={!showStructures}
                        center={centerPosition}
                        zoom={DEFAULT_ZOOM}
                        style={{ width: "100%", height: "100%" }}
                        interactiveLayerIds={[
                            "ineligible-structure-layer",
                            "span-wspd-layer",
                            "sensor-placement-layer",
                            "problem-lines-icons-layer",
                        ]}
                        onClick={handleMapClick}
                    >
                        <Source id="ineligible-structures" type="geojson" data={ineligibleStructuresGeoJSON}>
                            {/* Red ✕ on structures that are ineligible for sensor placement */}
                            <Layer
                                id="ineligible-structure-layer"
                                type="symbol"
                                paint={{
                                    "text-color": "#d32f2f",
                                    "text-halo-color": "#ffffff",
                                    "text-halo-width": 2,
                                    "text-opacity": showStructures ? 1 : 0,
                                }}
                                layout={{
                                    "text-field": "✕",
                                    "text-size": 24,
                                    "text-allow-overlap": true,
                                    "text-ignore-placement": true,
                                }}
                            />
                        </Source>
                        <Source
                            id="accessible-override-structures"
                            type="geojson"
                            data={accessibleOverrideStructuresGeoJSON}
                        >
                            {/* Faded ✕ on structures overridden back to eligible after being ineligible */}
                            <Layer
                                id="newly-reviewed-eligible-structure-layer"
                                type="symbol"
                                paint={{
                                    "text-color": "#d32f2f",
                                    "text-halo-color": "#ffffff",
                                    "text-halo-width": 2,
                                    "text-opacity": showStructures ? 0.25 : 0,
                                }}
                                layout={{
                                    "text-field": "✕",
                                    "text-size": 24,
                                    "text-allow-overlap": true,
                                    "text-ignore-placement": true,
                                }}
                            />
                        </Source>
                        <Source
                            id="all-spans"
                            type="geojson"
                            data={showSpanWindSpeed ? spanWspdGeoJSON : { type: "FeatureCollection", features: [] }}
                        >
                            {/* All spans colored by their wind speed; shown when the Wind Speed toggle is on */}
                            <Layer
                                id="span-wspd-layer"
                                type="line"
                                beforeId="cad-structure-layer"
                                paint={{
                                    "line-color": ["get", "span_color"],
                                    "line-width": 3,
                                    "line-offset": ["get", "offset"],
                                }}
                            />
                        </Source>
                        <Source id="sensor-spans" type="geojson" data={allSensorSpansGeoJSON}>
                            {/* White dashed lines from each sensor's placement structure to its chosen span endpoint */}
                            <Layer
                                id="sensor-spans-layer"
                                type="line"
                                beforeId="cad-structure-layer"
                                paint={{
                                    "line-color": showSpanWindSpeed ? "#ffffff" : "#000000",
                                    "line-width": 3,
                                    "line-dasharray": [4, 2],
                                }}
                            />
                        </Source>
                        <Source id="problem-lines-spans" type="geojson" data={problemLinesSpansGeoJSON}>
                            {/* Orange spans: dark orange = uncoverable lines, regular orange = uncovered lines */}
                            <Layer
                                id="problem-lines-spans-layer"
                                type="line"
                                beforeId="cad-structure-layer"
                                paint={{
                                    "line-color": ["get", "span_color"],
                                    "line-width": 5,
                                    "line-offset": ["get", "offset"],
                                }}
                            />
                        </Source>
                        <Source id="problem-lines-icons" type="geojson" data={problemLinesIconsGeoJSON}>
                            <Layer
                                id="problem-lines-icons-layer"
                                type="symbol"
                                layout={{
                                    "text-field": "▲",
                                    "text-size": 20,
                                    "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
                                    "text-anchor": "center",
                                    "text-allow-overlap": true,
                                    "text-ignore-placement": true,
                                }}
                                paint={{
                                    "text-color": ["get", "icon_color"],
                                    "text-halo-color": "#ffffff",
                                    "text-halo-width": 2,
                                }}
                            />
                        </Source>
                        <Source id="all-coverage-spans" type="geojson" data={allCoverageSpansGeoJSON}>
                            {/* All spans colored green (covered) or red (uncovered) */}
                            <Layer
                                id="all-coverage-spans-layer"
                                type="line"
                                beforeId="cad-structure-layer"
                                paint={{
                                    "line-color": ["get", "span_color"],
                                    "line-width": 5,
                                    "line-offset": ["get", "offset"],
                                }}
                            />
                        </Source>
                        <Source id="wspd-highlight-spans" type="geojson" data={wspdHighlightGeoJSON}>
                            {/* Spans in the selected low-wspd percentile bucket, highlighted dark blue */}
                            <Layer
                                id="wspd-highlight-spans-layer"
                                type="line"
                                beforeId="cad-structure-layer"
                                paint={{
                                    "line-color": ["get", "span_color"],
                                    "line-width": 5,
                                    "line-offset": ["get", "offset"],
                                }}
                            />
                        </Source>
                        <Source id="wspd-highlight-sensors" type="geojson" data={wspdHighlightSensorsGeoJSON}>
                            {/* Cyan ring around sensors whose wind speed falls in the active low-wspd bucket */}
                            <Layer
                                id="wspd-highlight-sensors-layer"
                                type="circle"
                                paint={{
                                    "circle-radius": 20,
                                    "circle-color": "rgba(0, 188, 212, 0.2)",
                                    "circle-stroke-color": "#00bcd4",
                                    "circle-stroke-width": 2,
                                }}
                            />
                        </Source>
                        <Source id="sensor-placements" type="geojson" data={sensorPlacementGeoJSON}>
                            {/* star at each sensor's current (or pending override) placement structure */}
                            <Layer
                                id="sensor-placement-layer"
                                type="symbol"
                                paint={{
                                    "text-color": ["get", "sensor_color"],
                                    "text-halo-color": "#ffffff",
                                    "text-halo-width": 1.5,
                                }}
                                layout={{
                                    "text-field": "■",
                                    "text-size": 36,
                                    "text-allow-overlap": true,
                                    "text-ignore-placement": true,
                                }}
                            />
                            {/* Sensor name label centered inside the star */}
                            <Layer
                                id="sensor-placement-label-layer"
                                type="symbol"
                                paint={{
                                    "text-color": "#ffffff",
                                }}
                                layout={{
                                    "text-field": ["get", "sensor_name"],
                                    "text-size": 9,
                                    "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
                                    "text-allow-overlap": true,
                                    "text-ignore-placement": true,
                                    "text-anchor": "center",
                                    "text-offset": [0, 0],
                                }}
                            />
                        </Source>
                        <Source id="original-placements" type="geojson" data={originalPlacementGeoJSON}>
                            {/* Faded ★ at the original placement structure while a structure override is being previewed */}
                            <Layer
                                id="original-placement-layer"
                                type="symbol"
                                paint={{
                                    "text-color": ["get", "sensor_color"],
                                    "text-opacity": 0.5,
                                }}
                                layout={{
                                    "text-field": "■",
                                    "text-size": 36,
                                    "text-allow-overlap": true,
                                    "text-ignore-placement": true,
                                }}
                            />
                        </Source>
                        <Source id="selected-structures" type="geojson" data={selectedStructuresGeoJSON}>
                            {/* Gold ring around structures currently selected via box-select */}
                            <Layer
                                id="selected-structure-highlight-layer"
                                type="circle"
                                paint={{
                                    "circle-radius": 15,
                                    "circle-color": "rgba(255, 215, 0, 0.3)",
                                    "circle-stroke-color": "#FFD700",
                                    "circle-stroke-width": 2,
                                }}
                            />
                        </Source>
                        <ScaleControl position="bottom-right" />
                        <Source id="inaccessible-structures" type="geojson" data={inaccessibleStructuresGeoJSON}>
                            {/* Red ✕ on structures manually marked inaccessible during refinement */}
                            <Layer
                                id="inaccessible-structure-layer"
                                type="symbol"
                                paint={{
                                    "text-color": "#d32f2f",
                                    "text-halo-color": "#ffffff",
                                    "text-halo-width": 2,
                                    "text-opacity": showStructures ? 1 : 0,
                                }}
                                layout={{
                                    "text-field": "✕",
                                    "text-size": 24,
                                    "text-allow-overlap": true,
                                    "text-ignore-placement": true,
                                }}
                            />
                        </Source>
                    </ClientAssetDataMap>
                    {boxSelectMode && (
                        <div
                            style={{ position: "absolute", inset: 0, zIndex: 2, cursor: "crosshair" }}
                            onMouseDown={handleOverlayMouseDown}
                            onMouseMove={handleOverlayMouseMove}
                            onMouseUp={handleOverlayMouseUp}
                            onWheel={handleOverlayWheel}
                        />
                    )}
                    {boxSelectMode && dragStart && dragCurrent && (
                        <div
                            style={{
                                position: "absolute",
                                left: Math.min(dragStart.x, dragCurrent.x),
                                top: Math.min(dragStart.y, dragCurrent.y),
                                width: Math.abs(dragCurrent.x - dragStart.x),
                                height: Math.abs(dragCurrent.y - dragStart.y),
                                border: "2px dashed #1976d2",
                                backgroundColor: "rgba(25, 118, 210, 0.1)",
                                pointerEvents: "none",
                                zIndex: 3,
                            }}
                        />
                    )}
                    <div
                        style={{
                            position: "absolute",
                            bottom: 30,
                            left: 10,
                            zIndex: 4,
                            display: "flex",
                            flexDirection: "column",
                            gap: "6px",
                        }}
                    >
                        {spanWspdGeoJSON.features.length > 0 && (
                            <>
                                {showSpanWindSpeed && (
                                    <div
                                        style={{
                                            backgroundColor: "rgba(255,255,255,0.9)",
                                            border: "1px solid #cccccc",
                                            borderRadius: "4px",
                                            padding: "6px 10px",
                                            fontSize: "11px",
                                        }}
                                    >
                                        <div style={{ marginBottom: "4px", fontWeight: "bold" }}>Wind Speed (m/s)</div>
                                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                            <span>{minSpanWindSpeed.toFixed(1)}</span>
                                            <div
                                                style={{
                                                    width: "100px",
                                                    height: "10px",
                                                    borderRadius: "2px",
                                                    background: WSPD_COLOR_KEY,
                                                }}
                                            />
                                            <span>{maxSpanWindSpeed.toFixed(1)}</span>
                                        </div>
                                    </div>
                                )}
                                <Button
                                    variant={showSpanWindSpeed ? "contained" : "outlined"}
                                    size="small"
                                    onClick={() => setShowSpanWindSpeed((v) => !v)}
                                    style={{ backgroundColor: showSpanWindSpeed ? undefined : "white" }}
                                >
                                    Toggle Span Wspd
                                </Button>
                            </>
                        )}
                        <Button
                            variant={showStructures ? "contained" : "outlined"}
                            size="small"
                            onClick={() => setShowStructures((v) => !v)}
                            style={{ backgroundColor: showStructures ? undefined : "white" }}
                        >
                            Toggle Structures
                        </Button>
                    </div>
                    <div
                        style={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            zIndex: 4,
                            display: "flex",
                            gap: "6px",
                        }}
                    >
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                            {boxSelectMode ? (
                                <>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => {
                                            onSubmitRefinement({
                                                newly_reviewed_inaccessible: [...ineligibleStructureFromUser],
                                                newly_reviewed_eligible: [...accessibleStructureFromUser],
                                            });
                                            setIneligibleStructureFromUser(new Set());
                                            setAccessibleStructureFromUser(new Set());
                                            setBoxSelectMode(false);
                                            dragStartRef.current = null;
                                            setDragStart(null);
                                            setDragCurrent(null);
                                        }}
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        style={{ backgroundColor: "white" }}
                                        onClick={() => {
                                            setIneligibleStructureFromUser(new Set());
                                            setAccessibleStructureFromUser(new Set());
                                            setBoxSelectMode(false);
                                            dragStartRef.current = null;
                                            setDragStart(null);
                                            setDragCurrent(null);
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        color="error"
                                        style={{ backgroundColor: "white" }}
                                        onClick={() => {
                                            setIneligibleStructureFromUser(new Set());
                                            setAccessibleStructureFromUser(new Set());
                                        }}
                                    >
                                        Clear All ({ineligibleStructureFromUser.size})
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => {
                                            setBoxSelectMode(true);
                                            setShowStructures(true);
                                        }}
                                        style={{ backgroundColor: "white" }}
                                    >
                                        Mark Inaccessible Structures
                                    </Button>
                                    {ineligibleStructureFromUser.size > 0 && (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            color="error"
                                            onClick={() => setIneligibleStructureFromUser(new Set())}
                                            style={{ backgroundColor: "white" }}
                                        >
                                            Clear All Inaccessible ({ineligibleStructureFromUser.size})
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>
                        {selectedStructureNames.size > 0 && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setSelectedStructureNames(new Set())}
                                style={{ backgroundColor: "white" }}
                            >
                                Clear ({selectedStructureNames.size})
                            </Button>
                        )}
                        {selectedStructureNames.size > 0 &&
                            (() => {
                                const markableCount = [...selectedStructureNames].filter(
                                    (n) => !ineligibleStructureFromUser.has(n)
                                ).length;
                                return markableCount > 0 ? (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        color="error"
                                        onClick={() => {
                                            setIneligibleStructureFromUser(
                                                (prev) =>
                                                    new Set([
                                                        ...prev,
                                                        ...[...selectedStructureNames].filter((n) => !prev.has(n)),
                                                    ])
                                            );
                                            setSelectedStructureNames(new Set());
                                        }}
                                        style={{ backgroundColor: "white" }}
                                    >
                                        Mark Inaccessible ({markableCount})
                                    </Button>
                                ) : null;
                            })()}
                        {ineligibleStructureFromUser.size > 0 &&
                            (() => {
                                const selectedInaccessibleCount = [...selectedStructureNames].filter((n) =>
                                    ineligibleStructureFromUser.has(n)
                                ).length;
                                return (
                                    <>
                                        {selectedInaccessibleCount > 0 && (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                color="error"
                                                onClick={() => {
                                                    setIneligibleStructureFromUser(
                                                        (prev) =>
                                                            new Set(
                                                                [...prev].filter((n) => !selectedStructureNames.has(n))
                                                            )
                                                    );
                                                    setSelectedStructureNames(new Set());
                                                }}
                                                style={{ backgroundColor: "white" }}
                                            >
                                                Clear Inaccessible ({selectedInaccessibleCount})
                                            </Button>
                                        )}
                                    </>
                                );
                            })()}
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleZoomToFit}
                            style={{ backgroundColor: "white" }}
                        >
                            Zoom to Fit
                        </Button>
                    </div>
                </>
            ) : (
                <div
                    style={{
                        width: "100%",
                        height: "100%",
                        textAlign: "center",
                        border: "1px solid #dddddd",
                        backgroundColor: "#eeeeee",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <b>Select a client and project to load the map</b>
                </div>
            )}
        </div>
    );
}
